import { ClarionButton } from '@clarion/ClarionButton';
import { ClarionButtonLink } from '@clarion/ClarionButtonLink';
import { ClarionCard } from '@clarion/ClarionCard';
import { Icon } from '@clarion/ClarionIcon';
import { ClarionNonIdealState } from '@clarion/ClarionNonIdealState';
import { captureException } from '@sentry/react';
import { lazy, useEffect } from 'react';
import { useRouteError } from 'react-router';
import { contactLink } from '../account/FeedbackButtons';
import recapLogo from '../shared/recapLogo.svg';
import supportLinks from '../shared/supportLinks';
import NonIdealPage from './NonIdealPage';

const NotFound = lazy(() => import('./NotFoundPage').then((m) => ({ default: m.Component })));

/**
 * Detects if an error is caused by a failed dynamic module import,
 * which typically happens when the user is on an old version of the app
 * and we've deployed new assets.
 */
function isModuleLoadError(error: unknown): boolean {
  const errorMessage = String((error as any)?.message || '').toLowerCase();
  return (
    navigator.onLine &&
    (errorMessage.includes('dynamically imported module') ||
      (errorMessage.includes('fetch') && errorMessage.includes('module')))
  );
}

export function RouterErrorBoundary() {
  const error = useRouteError() as Error;
  useEffect(() => {
    console.error(error);
    // Don't send module loading errors to Sentry - these are expected during deployments
    if (!isModuleLoadError(error)) {
      captureException(error);
    }
  }, [error]);

  return (
    <ErrorView error={error} debugInfo={`${JSON.stringify((error as any)?.message || error)}`} />
  );
}

export function ErrorView({ error, debugInfo }: { error?: unknown; debugInfo?: string }) {
  if (debugInfo?.toLowerCase().includes('not found')) {
    return <NotFound />;
  }

  // Check if this is a module loading error (deployment update)
  const isModuleError = error && isModuleLoadError(error);

  if (isModuleError) {
    return (
      <NonIdealPage
        icon={<Icon icon="updated" intent="success" size={48} />}
        title="App updated!"
        description="We've released new features and improvements. Please reload the page to get the latest version."
        action={
          <ClarionButton onClick={() => window.location.reload()} intent="primary">
            Reload to update
          </ClarionButton>
        }
      />
    );
  }

  // Generic error view for all other errors
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <ClarionCard className="max-w-md w-full">
        <div className="flex flex-col items-center mb-6 text-balance">
          <ClarionNonIdealState
            icon={<Icon icon="error" intent="danger" size={48} />}
            title="Sorry, something went wrong."
            description="This may be temporary. Try reloading the page, or contact us if the issue persists."
          />
        </div>
        <div className="flex flex-col gap-3">
          <ClarionButton onClick={() => window.location.reload()} intent="primary">
            Reload Page
          </ClarionButton>
          <ClarionButtonLink
            icon="send-message"
            children="Report a Problem"
            target="_blank"
            href={contactLink(
              'Recap Error Report',
              'Please retain the information below. You may include any additional information, such as actions taken before the error happened.',
              `${window.location.pathname}  ${debugInfo}`,
            )}
          />
        </div>
        <details className="mt-4">
          <summary className={`cursor-pointer opacity-80`}>Error Details</summary>
          <ClarionCard className="mt-2 p-0!" compact>
            <pre className="p-3 whitespace-pre-wrap overflow-auto">{debugInfo}</pre>
          </ClarionCard>
        </details>
      </ClarionCard>
      <a
        href={supportLinks.about}
        target="_blank"
        className="flex items-center gap-2 mt-6"
        rel="noreferrer"
      >
        <img src={recapLogo} alt="Recap" className="h-6" />
        <span className={`text-sm`}>Recap Innovations</span>
      </a>
    </div>
  );
}
